import React, { useContext, memo } from "react";
import {
  Header as MantineHeader,
  MediaQuery,
  Burger,
  useMantineTheme,
  Group,
  Text,
  MantineNumberSize,
  Flex,
  Image,
} from "@mantine/core";
import { AppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";
import { BsDot } from "react-icons/bs";
import { useStyles } from "./styles";
import { useHeaderContext } from "src/api/context/HeaderContext";
import { BiArrowBack } from "react-icons/bi";

interface HeaderProps {
  setNavbarOpened: React.Dispatch<React.SetStateAction<boolean>>;
  isNavbarOpen: boolean;
  hideNavMenu?: boolean;
  navbarOffsetBreakpoint?: MantineNumberSize | undefined;
}

const Header = ({
  setNavbarOpened,
  isNavbarOpen,
  hideNavMenu,
  navbarOffsetBreakpoint,
}: HeaderProps) => {
  const { headerRightContent, isBackIcon } = useHeaderContext();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { currentPageTitle, currentClient } = useContext(AppContext);
  const { classes } = useStyles();

  return (
    <MantineHeader
      height={75}
      px={15}
      pt={15}
      className={classes.header}
      style={{ backgroundColor: theme.colors.secondary[6] }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "fit-content",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Group position="left" spacing="xs">
          {!hideNavMenu && (
            <MediaQuery
              largerThan={navbarOffsetBreakpoint}
              styles={{ display: "none" }}
            >
              <Flex
                sx={{
                  borderRadius: 8,
                  padding: isBackIcon ? 8 : 4,
                  backgroundColor: !isNavbarOpen
                    ? theme.colors.primary[6]
                    : "transparent",
                }}
              >
                {isBackIcon ? (
                  <BiArrowBack
                    size={20}
                    onClick={() => navigate(-1)}
                    color={theme.colors.primaryTextColor[6]}
                  />
                ) : (
                  <Burger
                    color={
                      isNavbarOpen
                        ? theme.colors.secondaryTextColor[6]
                        : theme.colors.primaryTextColor[6]
                    }
                    onClick={() => setNavbarOpened((isOpen) => !isOpen)}
                    opened={isNavbarOpen}
                    size="md"
                  />
                )}
              </Flex>
            </MediaQuery>
          )}
          <Group position="left" spacing={0}>
            {!!currentClient?.organization?.logoUrl && (
              <Image
                className={!isNavbarOpen ? classes.orgName : ""}
                width={50}
                height={50}
                radius={40}
                src={currentClient?.organization.logoUrl}
                alt="Org Logo"
              />
            )}
            <Text
              className={!isNavbarOpen ? classes.orgName : ""}
              size={20}
              weight={600}
              onClick={() => navigate("/")}
              sx={{
                paddingLeft: 8,
                paddingRight: 8,
                cursor: "pointer",
                color: theme.colors.secondaryTextColor[6],
                "&:hover": {
                  borderRadius: 20,
                  backgroundColor: theme.colors.secondary[4],
                },
              }}
            >
              {currentClient?.organization?.name}
            </Text>
            {!isNavbarOpen && (
              <>
                {currentPageTitle && (
                  <BsDot
                    className={classes.dot}
                    color={theme.colors.secondaryTextColor[6]}
                  />
                )}
                <Text
                  size={22}
                  weight={400}
                  color={theme.colors.secondaryTextColor[6]}
                  sx={{
                    paddingLeft: 8,
                    paddingRight: 8,
                  }}
                >
                  {currentPageTitle}
                </Text>
              </>
            )}
          </Group>
        </Group>
        <Group position="right" style={{ flex: 1, height: "100%" }}>
          <>{!isNavbarOpen && headerRightContent}</>
        </Group>
      </div>
    </MantineHeader>
  );
};

export default memo(Header);
